import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  useColorModeValue,
  Box,
  Badge,
  IconButton,
  Center,
  Button,
  Select,
  Stack,
  Icon,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useBreakpointValue,
  Divider,
} from "@chakra-ui/react";
import { db } from "lib/firebase";
import { collection, getDoc, doc, updateDoc } from "firebase/firestore";
import { useStore } from "@nanostores/react";
import { userProfile } from "contexts/userProfile";
import {
  FaDownload,
  FaPenAlt,
  FaTimes,
  FaUser,
  FaSignature,
  FaEye,
  FaExclamationCircle,
  FaInfoCircle,
  FaBook,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HelloSign from "hellosign-embedded";

const clientId = process.env.REACT_APP_DROPBOX_CLIENT;

function SignatureRequests() {
  const { documents } = useStore(userProfile);
  const { ownerData, signersData } = documents;
  const [currentSignature, setCurrentSignature] = useState(null);
  const [isDownloadAvailable, setDownloadAvailable] = useState(false);
  const [insightsData, setInsightsData] = useState({
    keyTakeaways: [],
    riskyClauses: [],
    summary: "",
  });
  const [isInsightsModalOpen, setInsightsModalOpen] = useState(false);

  const [filterType, setFilterType] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const combinedData = [...signersData, ...ownerData].sort((a, b) => {
    const timeA = a.createdTime || a.ownerCreatedTime;
    const timeB = b.createdTime || b.ownerCreatedTime;
    return new Date(timeB) - new Date(timeA);
  });

  const totalPages = Math.ceil(
    combinedData.filter((item) => {
      if (filterType === "signers") return item.documentName;
      if (filterType === "owners") return item.ownerDocumentName;
      return true;
    }).length / itemsPerPage
  );

  const currentData = useMemo(() => {
    const filteredData = combinedData.filter((item) => {
      if (filterType === "signers") return item.documentName;
      if (filterType === "owners") return item.ownerDocumentName;
      return true;
    });
    return filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [combinedData, currentPage, itemsPerPage, filterType]);

  const borderColor = useColorModeValue("gray.200", "whiteAlpha.300");
  const textColor = useColorModeValue("gray.700", "white");
  const bgColor = useColorModeValue("gray.50", "gray.800");
  const {
    data: { uid, email: userEmail },
  } = useStore(userProfile);

  const getStatusBadge = (status) => {
    let color, label;

    switch (status) {
      case "awaiting_signature":
        color = "yellow";
        label = "Pending";
        break;
      case "success":
        color = "green";
        label = "Success";
        break;
      case "rejected":
        color = "red";
        label = "Rejected";
        break;
      default:
        color = "gray";
        label = "N/A";
        break;
    }

    return (
      <Center>
        <Badge
          bg={`${color}.100`}
          color={`${color}.400`}
          fontSize="sm"
          px="8px"
          w="fit-content"
          h="25px"
          borderRadius="full"
          display="flex"
          alignItems="center"
          justifyContent="center"
          mr="5px"
        >
          {label}
        </Badge>
      </Center>
    );
  };

  const handleSignWithSignUrl = async (signatureId, signatureRequestId) => {
    try {
      const response = await fetch("https://api-seyimfnmxq-uc.a.run.app/getEmbeddedSignature", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signatureId }),
      });

      if (!response.ok) {
        console.error("Error fetching sign_url:", response.statusText);
        toast.error("Error signing the document. Please try again.", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      const responseData = await response.json();
      const signUrl = responseData?.data?.embedded?.signUrl;

      const client = new HelloSign({
        clientId: clientId,
      });

      client.open(signUrl, {
        skipDomainVerification: true,
      });

      client.on("sign", async () => {
        try {
          const signaturesCollectionRef = collection(db, "signatures");
          const signatureRequestDocRef = doc(signaturesCollectionRef, signatureRequestId);
          const signatureRequestDocSnapshot = await getDoc(signatureRequestDocRef);

          if (signatureRequestDocSnapshot.exists()) {
            const signatureData = signatureRequestDocSnapshot.data();

            if (signatureData && signatureData.signers) {
              let signers = signatureData.signers;

              for (let key in signers) {
                if (signers[key].signatureId === signatureId) {
                  signers[key].statusCode = "success";
                  break;
                }
              }

              await updateDoc(signatureRequestDocRef, { signers: signers });

              toast.success(`Document successfully signed.`, {
                position: "top-right",
                autoClose: 3000,
              });
            } else {
              console.error(`No signers found for signature request ID ${signatureRequestId}`);
            }
          } else {
            console.error(`No signature request found with ID ${signatureRequestId}`);
          }
        } catch (error) {
          console.error("Error updating signature status:", error);
          toast.error("Error signing the document. Please try again.", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      });
    } catch (error) {
      console.error("Error handling sign with sign_url:", error);
      toast.warning("Please wait for the other signatory to complete their signature.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
      });
    }
  };

  const getSignButtons = (status, signatureId, signatureRequestId) => {
    if (status === "awaiting_signature") {
      return (
        <>
          <IconButton
            aria-label="Sign"
            icon={<FaPenAlt />}
            fontSize={"13px"}
            textColor={"green.400"}
            bgColor={"green.100"}
            onClick={() => handleSignWithSignUrl(signatureId, signatureRequestId)}
            mr={2}
          />
          <IconButton
            aria-label="Reject"
            icon={<FaTimes />}
            fontSize={"13px"}
            textColor={"red.400"}
            bgColor={"red.100"}
            onClick={() => handleReject(signatureRequestId)}
          />
        </>
      );
    }
  };

  const handleReject = async (signatureRequestId) => {
    try {
      const signaturesCollectionRef = collection(db, "signatures");
      const signatureRequestDocRef = doc(signaturesCollectionRef, signatureRequestId);
      const signatureRequestDocSnapshot = await getDoc(signatureRequestDocRef);

      if (signatureRequestDocSnapshot.exists()) {
        const signatureData = signatureRequestDocSnapshot.data();

        if (signatureData && signatureData.signers) {
          let signers = signatureData.signers;

          for (let key in signers) {
            signers[key].statusCode = "rejected";
          }

          await updateDoc(signatureRequestDocRef, { signers: signers });

          console.log(
            `All signers for signature request ID ${signatureRequestId} marked as 'rejected'`
          );
          toast.success("E-signature request rejected.", {
            position: "top-right",
            autoClose: 3000,
          });
        } else {
          console.error(`No signers found for signature request ID ${signatureRequestId}`);
        }
      } else {
        console.error(`No signature request found with ID ${signatureRequestId}`);
      }
    } catch (error) {
      console.error("Error updating signers' status:", error);
    }
  };

  const handleDownload = async (signatureRequestId) => {
    try {
      const response = await fetch("https://api-seyimfnmxq-uc.a.run.app/downloadSignedDocument", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ signatureRequestId: signatureRequestId }),
      });

      if (!response.ok) {
        toast.error("Error downloading the document. Please try again.", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      const text = await response.text();
      const responseData = JSON.parse(text);
      const fileUrl = responseData?.data?.fileUrl;
      window.location.href = fileUrl;
      toast.success("Downloaded Successfully!", { position: "top-right", autoClose: 3000 });
    } catch (error) {
      console.error("Error during download:", error);
      toast.error("Error downloading the document. Please try again.", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const findSignerByEmail = useCallback((signersObject, emailToFind) => {
    for (const key in signersObject) {
      if (
        signersObject.hasOwnProperty(key) &&
        signersObject[key].signerEmailAddress === emailToFind
      ) {
        return signersObject[key];
      }
    }
    return null;
  }, []);

  const handleShowInsights = async (signatureRequestId) => {
    try {
      const signaturesCollectionRef = collection(db, "signatures");
      const signatureRequestDocRef = doc(signaturesCollectionRef, signatureRequestId);
      const signatureRequestDocSnapshot = await getDoc(signatureRequestDocRef);

      if (signatureRequestDocSnapshot.exists()) {
        const signatureData = signatureRequestDocSnapshot.data();

        // Extract key takeaways in order
        const keyTakeawaysArray = Object.keys(signatureData.insights.keyTakeaways || {})
          .sort((a, b) => {
            const numA = parseInt(a.replace("takeaway", ""), 10);
            const numB = parseInt(b.replace("takeaway", ""), 10);
            return numA - numB;
          })
          .map((key) => signatureData.insights.keyTakeaways[key]);

        // Extract risky clauses in order
        const riskyClausesArray = Object.keys(signatureData.insights.riskyClauses || {})
          .sort((a, b) => {
            const numA = parseInt(a.replace("clause", ""), 10);
            const numB = parseInt(b.replace("clause", ""), 10);
            return numA - numB;
          })
          .map((key) => signatureData.insights.riskyClauses[key]);

        setInsightsData({
          keyTakeaways: keyTakeawaysArray,
          riskyClauses: riskyClausesArray,
          summary: signatureData.insights.summary.summaryText,
        });

        const updatedSigners = signatureData.signers;
        const currentSigner = findSignerByEmail(updatedSigners, userEmail);
        if (currentSigner) {
          currentSigner.pendingReview = true;
        }
        await updateDoc(signatureRequestDocRef, {
          signers: updatedSigners,
        });

        // Open the modal
        setInsightsModalOpen(true);
      } else {
        console.error(`No signature request found with ID ${signatureRequestId}`);
      }
    } catch (error) {
      console.error("Error fetching insights:", error);
      toast.error("Error fetching insights. Please try again.", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px", lg: "100px" }}>
      <Box bg={bgColor} p={4} borderRadius="md" shadow="sm" overflowX="auto">
        <Stack direction="row" justify="space-between" align="center" my={2}>
          <Stack direction="row" spacing={4} align="center">
            <Flex align="center">
              <Text fontSize="sm" fontWeight="medium" mr={2}>
                Show:
              </Text>
              <Select
                size="sm"
                width="120px"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                variant="outline"
              >
                <option value="5">5 entries</option>
                <option value="10">10 entries</option>
                <option value="15">15 entries</option>
                <option value="20">20 entries</option>
              </Select>
            </Flex>
            <Flex align="center">
              <Text fontSize="sm" fontWeight="medium" mr={2}>
                Filter by:
              </Text>
              <Select
                size="sm"
                width="140px"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                variant="outline"
              >
                <option value="all">All</option>
                <option value="signers">To Sign</option>
                <option value="owners">Owner</option>
              </Select>
            </Flex>
          </Stack>

          <Stack direction="row" align="center">
            <Button
              isDisabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              Previous
            </Button>
            <Text>
              Page {currentPage} of {totalPages}
            </Text>
            <Button
              isDisabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              Next
            </Button>
          </Stack>
        </Stack>
        <Table variant="simple" color={textColor}>
          <Thead>
            <Tr my=".8rem" pl="0px" color="gray.400">
              <Th
                pl="0px"
                borderColor={borderColor}
                color="gray.400"
                textAlign="center"
                w="40px"
              ></Th>
              <Th pl="0px" borderColor={borderColor} color="gray.400" textAlign="center">
                Document Name
              </Th>
              <Th borderColor={borderColor} color="gray.400" textAlign="center">
                Created At
              </Th>
              <Th borderColor={borderColor} color="gray.400" textAlign="center">
                Sign Status
              </Th>
              <Th borderColor={borderColor} color="gray.400" textAlign="center">
                Signing Progress
              </Th>
              <Th borderColor={borderColor} color="gray.400" textAlign="center">
                Actions
              </Th>
            </Tr>
          </Thead>
          <Tbody justifyContent={"center"}>
            {currentData.length === 0 ? (
              <Tr>
                <Td colSpan={5} borderColor={borderColor} textAlign="center">
                  No Data
                </Td>
              </Tr>
            ) : (
              currentData.map((item, index) => {
                if (item.documentName) {
                  return (
                    <Tr key={index}>
                      <Td borderColor={borderColor} textAlign="center" w="40px">
                        <Center>
                          <Icon as={FaSignature} color={"green.500"} w={5} h={5} />
                        </Center>
                      </Td>
                      <Td pl="0px" borderColor={borderColor} textAlign="center">
                        {item.documentName}
                      </Td>
                      <Td borderColor={borderColor} textAlign="center">
                        {item.createdTime}
                      </Td>
                      <Td borderColor={borderColor} textAlign="center">
                        {getStatusBadge(item.statusCode)}
                      </Td>
                      <Td borderColor={borderColor} textAlign="center">
                        {`${item.signedCount}/${item.totalSigners}`}
                      </Td>
                      <Td borderColor={borderColor} textAlign="center">
                        {getSignButtons(item.statusCode, item.signatureId, item.signatureRequestId)}
                        {item.signedCount === item.totalSigners ? (
                          <IconButton
                            aria-label="Download"
                            icon={<FaDownload />}
                            fontSize={"13px"}
                            textColor={"green.400"}
                            bgColor={"green.100"}
                            onClick={() => handleDownload(item.signatureRequestId)}
                            ml={2}
                          />
                        ) : (
                          <IconButton
                            aria-label="Download"
                            icon={<FaDownload />}
                            fontSize={"13px"}
                            textColor={"gray.400"}
                            bgColor={"gray.100"}
                            isDisabled
                            ml={2}
                          />
                        )}
                        <IconButton
                          aria-label="See Insights"
                          icon={<FaEye />}
                          ml={2}
                          bgColor={item && item.pendingReview ? "blue.100" : "gray.200"}
                          onClick={() => handleShowInsights(item.signatureRequestId)}
                        />
                      </Td>
                    </Tr>
                  );
                } else if (item.ownerDocumentName) {
                  return (
                    <Tr key={index}>
                      <Td borderColor={borderColor} textAlign="center" w="40px">
                        <Center>
                          <Icon as={FaUser} color={"blue.400"} w={5} h={5} />
                        </Center>
                      </Td>
                      <Td pl="0px" borderColor={borderColor} textAlign="center">
                        {item.ownerDocumentName}
                      </Td>
                      <Td borderColor={borderColor} textAlign="center">
                        {item.ownerCreatedTime}
                      </Td>
                      <Td borderColor={borderColor} textAlign="center">
                        {getStatusBadge(item.ownerStatusCode)}
                      </Td>
                      <Td borderColor={borderColor} textAlign="center">
                        {`${item.ownerSignedCount}/${item.ownerTotalSigners}`}
                      </Td>
                      <Td borderColor={borderColor} textAlign="center">
                        {item.ownerStatusCode !== "rejected" &&
                          item.ownerSignedCount !== item.ownerTotalSigners && (
                            <IconButton
                              aria-label="Reject"
                              icon={<FaTimes />}
                              fontSize={"13px"}
                              textColor={"red.400"}
                              bgColor={"red.100"}
                              onClick={() => handleReject(item.ownerSignatureRequestId)}
                              ml={2}
                            />
                          )}
                        {item.ownerSignedCount === item.ownerTotalSigners ? (
                          <IconButton
                            aria-label="Download"
                            icon={<FaDownload />}
                            fontSize={"13px"}
                            textColor={"green.400"}
                            bgColor={"green.100"}
                            onClick={() => handleDownload(item.ownerSignatureRequestId)}
                            ml={2}
                          />
                        ) : (
                          <IconButton
                            aria-label="Download"
                            icon={<FaDownload />}
                            fontSize={"13px"}
                            textColor={"gray.400"}
                            bgColor={"gray.100"}
                            isDisabled
                            ml={2}
                          />
                        )}
                        <IconButton
                          aria-label="See Insights"
                          icon={<FaEye />}
                          ml={2}
                          bgColor={"blue.100"}
                          onClick={() => handleShowInsights(item.ownerSignatureRequestId)}
                        />
                      </Td>
                    </Tr>
                  );
                }
                return null;
              })
            )}
          </Tbody>
        </Table>

        <Modal isOpen={isInsightsModalOpen} onClose={() => setInsightsModalOpen(false)}>
          <ModalOverlay />
          <ModalContent
            p={5}
            fontFamily="Arial, sans-serif"
            maxW={useBreakpointValue({ base: "90%", md: "80%", lg: "70%" })}
            borderRadius="md"
            boxShadow="xl"
          >
            <ModalHeader fontSize="2xl" fontWeight="bold">
              Document Insights
            </ModalHeader>
            <ModalCloseButton />
            <Divider my={4} />

            <ModalBody>
              {insightsData.keyTakeaways && insightsData.keyTakeaways.length > 0 ? (
                <Box mb={4}>
                  <Flex alignItems="center">
                    <Box as={FaInfoCircle} mr={2} color="blue.500" w={4} h={4} />
                    <Text fontWeight="bold" fontSize="lg">
                      Key Takeaways:
                    </Text>
                  </Flex>
                  <ul>
                    {insightsData.keyTakeaways.map((takeaway, index) => (
                      <li key={index} style={{ marginBottom: "8px" }}>
                        {takeaway}
                      </li>
                    ))}
                  </ul>
                </Box>
              ) : (
                <Text>No key takeaways available.</Text>
              )}

              <Divider my={4} />

              {insightsData.riskyClauses && insightsData.riskyClauses.length > 0 ? (
                <Box mb={4}>
                  <Flex alignItems="center">
                    <Box as={FaExclamationCircle} mr={2} color="red.500" w={4} h={4} />
                    <Text fontWeight="bold" fontSize="lg">
                      Risky Clauses:
                    </Text>
                  </Flex>
                  <ul>
                    {insightsData.riskyClauses.map((clause, index) => (
                      <li key={index} style={{ marginBottom: "8px" }}>
                        {clause}
                      </li>
                    ))}
                  </ul>
                </Box>
              ) : (
                <Text>No risky clauses identified.</Text>
              )}

              <Divider my={4} />

              {/* Summary */}
              {insightsData.summary && (
                <Box>
                  <Flex alignItems="center">
                    <Box as={FaBook} mr={2} w={4} h={4} />
                    <Text fontWeight="bold" fontSize="lg" mb={2}>
                      Summary:
                    </Text>
                  </Flex>
                  <Text>{insightsData.summary}</Text>
                </Box>
              )}
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={() => setInsightsModalOpen(false)}>
                Close
              </Button>
              {currentSignature && (
                <Button
                  colorScheme="green"
                  // onClick={() =>
                  //   // handleSignWithSignUrl(
                  //   //   currentSignature.signatureId,
                  //   //   currentSignature.signatureRequestId
                  //   // )
                  // }
                >
                  eSign
                </Button>
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>
        <ToastContainer />
      </Box>
    </Flex>
  );
}

export default SignatureRequests;
